/**
 * Major incidents and their post-incident review — Step 54.
 *
 * **A major incident is a TYPE, not a flag.** The tracker settled this on
 * 2026-09-06 from Kai's own tenant (*"no Major Incident type"* in the
 * automation picker): a flag says "this one is big", a type says "this one is
 * a different THING with different fields" — an outage kind, a business
 * impact, impacted locations, a customer count and a review afterwards. His
 * usage is the second, and our `type` discriminator was designed for exactly
 * this ("a type per row, not a table per type").
 *
 * Which makes "promote to major incident" a **type conversion** — see
 * `ticketRef.ts`, where that rule had to stop being a symmetric set to
 * express it.
 *
 * Shared because the browser previews the review's publish gate while it is
 * being written and the server enforces it on publish. One function, two
 * runtimes, zero drift.
 */
export const OUTAGE_KINDS = ['full', 'partial', 'degradation'] as const;
export type OutageKind = (typeof OUTAGE_KINDS)[number];

export const OUTAGE_KIND_LABEL: Record<OutageKind, string> = {
  full: 'Full outage',
  partial: 'Partial outage',
  degradation: 'Performance degradation',
};

/** What a post-incident review holds. Text everywhere it is prose, because a
 *  PIR is read by people, not aggregated. */
export interface PirDraft {
  summary?: string | null;
  rootCause?: string | null;
  /** The narrative impact, separate from the outage record's structured
   *  `businessImpact` — the reference export carries both, one in the facts
   *  block and one in the write-up. */
  impact?: string | null;
  resolution?: string | null;
  wentWell?: string | null;
  wentBadly?: string | null;
}

/**
 * The review's sections, named as the reference export names them.
 *
 * They were "What happened", "How it was fixed", "What did not" — Kai:
 * *"the language across so much of this app is very personal or over
 * explained, not necessarily corporate."* A post-incident review is read by
 * people who did not attend the incident, quoted in meetings and attached to
 * emails; it wants the vocabulary those readers already use.
 *
 * Only three are required to publish. A review answers what was seen, why,
 * and what fixed it — everything else is valuable and none of it is worth
 * blocking publication over, because a gate nobody can satisfy on a Friday
 * evening produces no review at all.
 */
export interface TimelineEntry {
  at: number;
  text: string;
}

export const PIR_SECTIONS: {
  key: keyof PirDraft;
  label: string;
  required: boolean;
}[] = [
  { key: 'summary', label: 'Observed symptoms', required: true },
  { key: 'rootCause', label: 'Root cause', required: true },
  { key: 'impact', label: 'Impact', required: false },
  { key: 'resolution', label: 'Resolution steps', required: true },
  { key: 'wentWell', label: 'What went well', required: false },
  { key: 'wentBadly', label: 'What could be improved', required: false },
];

const has = (v: string | null | undefined) => !!v && v.trim().length > 0;

/** Which required sections are still empty — the UI shows this as a
 *  checklist, so "why is Publish disabled" never needs asking. */
export function pirMissing(pir: PirDraft): string[] {
  return PIR_SECTIONS.filter((s) => s.required && !has(pir[s.key])).map((s) => s.label);
}

/**
 * Null when the review may be published, else why not.
 *
 * Two conditions, and the first is the one people forget: a review written
 * while the incident is still running is a status update. It reads as settled
 * when it is not, and it will not be revisited.
 */
export function pirPublishGate(pir: PirDraft, opts: { stoodDownAt: number | null }): string | null {
  if (!opts.stoodDownAt) return 'Stand the incident down before publishing the review.';
  const missing = pirMissing(pir);
  if (missing.length) return `Required: ${missing.join(', ')}.`;
  return null;
}

/** How far along a draft is, for the progress line on the tab. Counts every
 *  section, not just the required ones — otherwise a review reads 100% while
 *  two thirds of it is blank. */
export function pirCompleteness(pir: PirDraft): number {
  const done = PIR_SECTIONS.filter((s) => has(pir[s.key])).length;
  return Math.round((done / PIR_SECTIONS.length) * 100);
}

/**
 * How long the incident ran, as a sentence.
 *
 * Business hours are deliberately NOT applied. An SLA clock pauses overnight
 * because nobody is expected to be working; an outage does not stop being an
 * outage at 5pm, and a PIR that said "lasted 2 hours" about something that
 * spanned a weekend would be false in the one document written to be quoted.
 */
export function outageDuration(declaredAt: number | null, stoodDownAt: number | null): string | null {
  if (!declaredAt || !stoodDownAt || stoodDownAt < declaredAt) return null;
  const mins = Math.round((stoodDownAt - declaredAt) / 60_000);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'}`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `${h}h ${m}m` : `${h} hour${h === 1 ? '' : 's'}`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ${h % 24}h`;
}

/**
 * A duration in the reference export's own words — "3 hours 14 minutes 17
 * seconds", "42 minutes 10 seconds", "7 seconds".
 *
 * Seconds are kept, unlike everywhere else in the product, because these are
 * response METRICS: "1 minute" and "1 minute 42 seconds" are different
 * answers to "how fast did we acknowledge it?", and rounding one into the
 * other flatters the number.
 */
export function formatDuration(ms: number | null): string | null {
  if (ms === null || ms < 0) return null;
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h} hour${h === 1 ? '' : 's'}`);
  if (m) parts.push(`${m} minute${m === 1 ? '' : 's'}`);
  // Only mention seconds when they are the whole answer or a real remainder.
  if (sec || !parts.length) parts.push(`${sec} second${sec === 1 ? '' : 's'}`);
  return parts.join(' ');
}

export interface IncidentTimes {
  /** When monitoring or a person first saw it. */
  detectedAt?: number | null;
  createdAt: number;
  /** First agent response — what acknowledgement means here. */
  acknowledgedAt?: number | null;
  declaredAt?: number | null;
  stoodDownAt?: number | null;
  resolvedAt?: number | null;
  closedAt?: number | null;
}

/**
 * The four response metrics the reference export prints.
 *
 * Each is null rather than zero when its inputs are missing — a blank cell
 * says "not recorded", and a 0 says "instant". On a document written to be
 * quoted, those must not look the same.
 */
export function incidentMetrics(t: IncidentTimes): {
  mttd: string | null;
  mtta: string | null;
  mttr: string | null;
  total: string | null;
  outage: string | null;
} {
  const gap = (from: number | null | undefined, to: number | null | undefined) =>
    from == null || to == null ? null : formatDuration(to - from);
  return {
    // Detect → raise. Needs someone to have said when it actually started.
    mttd: gap(t.detectedAt, t.createdAt),
    mtta: gap(t.createdAt, t.acknowledgedAt),
    mttr: gap(t.createdAt, t.resolvedAt),
    total: gap(t.createdAt, t.closedAt ?? t.resolvedAt),
    // The bit customers felt, which is not the ticket's lifetime.
    outage: gap(t.declaredAt, t.stoodDownAt),
  };
}

/**
 * The document layer — what the exported review shows, and what the author
 * has corrected.
 *
 * Kai: *"all the dates, times, bits of text etc need to be changeable"*, and
 * *"they might not be accurate"*. They might not: every fact below is derived
 * from timestamps recorded for operational reasons, and `resolved_at` in
 * particular moves whenever somebody reopens and re-resolves a ticket. His own
 * export showed a sixty-hour mean-time-to-resolve on an outage that lasted
 * seven seconds.
 *
 * Shared so the editor and the document cannot disagree about which facts
 * exist or what they are called — the failure mode being an override that
 * edits a key nothing renders.
 */
export const PIR_BLOCKS = [
  { key: 'details', label: 'Incident details' },
  { key: 'response', label: 'Incident response' },
  { key: 'summary', label: 'Incident summary' },
  { key: 'timeline', label: 'Timeline entries' },
  { key: 'actions', label: 'Action items' },
] as const;

export const PIR_FACTS: { key: string; label: string; block: 'details' | 'response' }[] = [
  { key: 'status', label: 'Status', block: 'details' },
  { key: 'priority', label: 'Priority', block: 'details' },
  { key: 'outageKind', label: 'Major incident type', block: 'details' },
  { key: 'slaBreached', label: 'SLA breached', block: 'details' },
  { key: 'customersImpacted', label: 'Customers impacted', block: 'details' },
  { key: 'assignee', label: 'Assigned to', block: 'details' },
  { key: 'manager', label: 'Major incident manager', block: 'details' },
  { key: 'group', label: 'Group', block: 'details' },
  { key: 'raisedBy', label: 'Raised by', block: 'details' },
  { key: 'detectedAt', label: 'Detected', block: 'response' },
  { key: 'createdAt', label: 'Raised', block: 'response' },
  { key: 'declaredAt', label: 'Declared major', block: 'response' },
  { key: 'stoodDownAt', label: 'Service restored', block: 'response' },
  { key: 'resolvedAt', label: 'Resolved', block: 'response' },
  { key: 'mttd', label: 'Mean time to detect', block: 'response' },
  { key: 'mtta', label: 'Mean time to acknowledge', block: 'response' },
  { key: 'mttr', label: 'Mean time to resolve', block: 'response' },
  { key: 'outage', label: 'Time in outage', block: 'response' },
];

export interface PirDocConfig {
  /** factKey → the author's wording. Text, so "about 20 minutes" is sayable. */
  overrides?: Record<string, string>;
  /** Fact and block keys to leave out of the document entirely. */
  hidden?: string[];
}

/**
 * What the document should print for a fact: the override if there is one,
 * otherwise what was derived.
 *
 * An EMPTY override is still an override — someone who clears the box is
 * saying "we do not have this", which is a different statement from the
 * derived value, and treating blank as "fall back" would make it impossible
 * to say.
 */
export function factValue(cfg: PirDocConfig | null | undefined, key: string, derived: string): string {
  const o = cfg?.overrides?.[key];
  return o === undefined ? derived : o;
}

/** True when the author has taken this fact or block out of the document. */
export function isHidden(cfg: PirDocConfig | null | undefined, key: string): boolean {
  return !!cfg?.hidden?.includes(key);
}

/** Has this fact been corrected? Drives the "edited" marker in the editor, so
 *  it is obvious at a glance which numbers are ours and which are the
 *  system's. */
export function isOverridden(cfg: PirDocConfig | null | undefined, key: string): boolean {
  return cfg?.overrides?.[key] !== undefined;
}
